/**
 * `useSignalRConnection` — React lifecycle wrapper for `SignalRClient`.
 *
 * Responsibilities (design.md §Frontend Layer › useSignalRConnection,
 * Requirements R4.5 / R4.6):
 *   - Own a single `SignalRClient` instance per hook usage. The factory
 *     is invoked once on first render and the resulting client is
 *     reused for the rest of the component lifetime. This keeps the
 *     underlying `HubConnection` (and its event subscriptions) stable
 *     across re-renders.
 *   - Mirror the client's `ConnectionState` into React state so
 *     components re-render on transition. The state-change listener is
 *     attached in an effect and torn down on unmount.
 *   - Always tear down: on unmount the hook calls `client.disconnect()`
 *     and unsubscribes the listener, so an orphaned WebSocket cannot
 *     outlive the component (R4.5 — "アンマウント後にイベント購読が
 *     残らない").
 *   - Expose `connect` / `disconnect` as stable references via
 *     `useCallback` so child components / dependency arrays are not
 *     disturbed by hook re-renders.
 *
 * Dependency direction (design.md §File Structure Plan):
 *   - This hook depends only on `../api/signalRClient`. It never
 *     imports REST or component code, preserving the
 *     `lib → api → hooks → components` topology.
 *
 * Testability:
 *   - The hook receives a `clientFactory` instead of constructing the
 *     client itself, so unit tests can inject a fake `SignalRClient`.
 *     Production callers will typically pass
 *     `() => createSignalRClient()`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type {
  ConnectNegotiate,
  ConnectionState,
  SignalRClient,
} from '../api/signalRClient';

// ---------------------------------------------------------------------------
// Public surface
// ---------------------------------------------------------------------------

/**
 * Result shape returned from `useSignalRConnection`.
 *
 * `client` is exposed so sibling hooks (e.g. `useMessages`, task 5.2) can
 * subscribe to `onNewMessage` / `onReadReceiptChanged` on the *same*
 * underlying connection without re-creating one.
 */
export interface UseSignalRConnectionResult {
  /** Live connection state, mirrored from the client. */
  readonly state: ConnectionState;
  /** Open the connection using the negotiate response. */
  readonly connect: (negotiate: ConnectNegotiate) => Promise<void>;
  /** Close the connection (returns to `idle`). */
  readonly disconnect: () => Promise<void>;
  /** The owned client. Stable identity for the entire hook lifetime. */
  readonly client: SignalRClient;
}

/**
 * React hook that owns a `SignalRClient` and surfaces its connection
 * state. The factory is invoked exactly once per hook usage.
 *
 * @param clientFactory  Called on first render to obtain the client. The
 *                       return value is captured in a ref so subsequent
 *                       renders reuse the same instance.
 */
export function useSignalRConnection(
  clientFactory: () => SignalRClient,
): UseSignalRConnectionResult {
  // -- Stable client instance --------------------------------------------
  // `useRef` with lazy initialization. We initialize on the first read
  // (instead of `useRef(clientFactory())`) to avoid re-invoking the
  // factory on every render. The `null` sentinel + null-check is the
  // idiomatic React-18 pattern for one-shot lazy refs that need access
  // to a closure (factory) only available at call time.
  const clientRef = useRef<SignalRClient | null>(null);
  if (clientRef.current === null) {
    clientRef.current = clientFactory();
  }
  const client = clientRef.current;

  // -- Mirror connection state into React state --------------------------
  // Seeded from the client's current state so callers see the right
  // value on the very first render (covers the "client created
  // pre-connected" edge case asserted in the tests).
  const [state, setState] = useState<ConnectionState>(client.state);

  useEffect(() => {
    // Subscribe to live transitions. The client guarantees that handlers
    // are invoked synchronously inside its `setState`, but React will
    // batch the resulting `setState` call as usual — no `act` warning
    // because we're already inside a controlled update path during
    // tests.
    //
    // Sync race guard: between render and effect, the client may have
    // already moved past the snapshot we seeded into React state (e.g.
    // a sibling hook called `connect` synchronously). Re-read here so
    // we don't display a stale value indefinitely.
    if (state !== client.state) {
      setState(client.state);
    }
    const unsubscribe = client.onConnectionStateChange(setState);
    return () => {
      unsubscribe();
    };
    // `client` identity is stable for the hook's lifetime (ref-backed),
    // so this effect runs exactly once on mount and tears down once on
    // unmount. We intentionally exclude `state` from the deps — the
    // race-guard above only needs to run on mount; subsequent updates
    // are driven by the subscription itself.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  // -- Disconnect on unmount ---------------------------------------------
  // Separate effect so the subscription teardown above is independent of
  // the disconnect call — even if `disconnect()` rejects, the listener
  // is still removed first.
  useEffect(() => {
    return () => {
      // Fire-and-forget. We can't await inside a cleanup function and
      // the component is already gone; any error here is purely an
      // observability concern (the underlying client logs it).
      void client.disconnect();
    };
  }, [client]);

  // -- Stable action callbacks ------------------------------------------
  const connect = useCallback(
    (negotiate: ConnectNegotiate) => client.connect(negotiate),
    [client],
  );
  const disconnect = useCallback(() => client.disconnect(), [client]);

  return { state, connect, disconnect, client };
}
